import React, { useState, Component } from 'react';
import emailjs from '@emailjs/browser';
import { Shield, Lock, Clock, User, Briefcase, Phone, Mail, DollarSign, CheckCircle, AlertCircle, TrendingUp, MapPin } from 'lucide-react';
interface LeadFormProps {
  language: 'en' | 'es';
}
export function LeadForm({
  language
}: LeadFormProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    phone: '',
    email: '',
    amount: '',
    monthlyRevenue: '',
    state: '',
    businessChecking: '',
    businessConfirmation: false
  });
  const [agreed, setAgreed] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const content = {
    en: {
      title: 'Get Pre-Qualified in Minutes',
      subtitle: 'Your business deserves a faster yes.',
      helperText: 'To qualify, your business should generate $10,000+ monthly and use a business checking account.',
      progressHint: 'Takes 2 minutes',
      fullName: 'Full Name',
      businessName: 'Business Name',
      phone: 'Phone Number',
      email: 'Email Address',
      amount: 'Requested Funding Amount',
      monthlyRevenue: 'Monthly Business Revenue',
      state: 'Business State',
      businessChecking: 'Do you have a business checking account?',
      businessConfirmation: 'I confirm this is a business funding request (not personal)',
      checkbox: 'I understand this will not affect my credit score',
      button: 'Get My Offer',
      buttonLoading: 'Sending...',
      noCreditPull: 'No Credit Pull',
      secure: 'Bank-Level Security',
      fast: '24-Hour Funding',
      selectAmount: 'Select funding amount',
      selectRevenue: 'Select monthly revenue',
      selectState: 'Select state',
      yes: 'Yes',
      no: 'No',
      reassurance: 'Same-day approvals. No hard credit pull. (Se habla español.)',
      successMessage: '✅ Request received! Check your email now — we have sent instructions to complete your same-day pre-approval. If you prefer, you can email your 3 most recent business bank statements directly to antony@sparrowadvance.com.',
      errorMessage: 'There was an error submitting your request. Please try again or contact us directly.',
      amounts: ['$25,000 - $75,000', '$75,000 - $150,000', '$150,000 - $500,000', '$500,000+'],
      revenues: ['$10,000 - $30,000', '$30,000 - $75,000', '$75,000 - $150,000', '$150,000+'],
      validationErrors: {
        required: 'This field is required.',
        email: 'Please enter a valid business email (e.g., info@company.com).',
        phone: 'Please enter a valid U.S. phone number.',
        name: 'Please enter a valid name.',
        revenue: 'Funding programs start at $10,000 in monthly business revenue.'
      }
    },
    es: {
      title: 'Pre-Califique en Minutos',
      subtitle: 'Su negocio merece un sí más rápido.',
      helperText: 'Para calificar, su negocio debe generar $10,000+ mensuales y usar una cuenta de cheques comercial.',
      progressHint: 'Toma 2 minutos',
      fullName: 'Nombre Completo',
      businessName: 'Nombre del Negocio',
      phone: 'Número de Teléfono',
      email: 'Correo Electrónico',
      amount: 'Cantidad de Financiamiento Solicitada',
      monthlyRevenue: 'Ingresos Mensuales del Negocio',
      state: 'Estado del Negocio',
      businessChecking: '¿Tiene una cuenta de cheques comercial?',
      businessConfirmation: 'Confirmo que esta es una solicitud de financiamiento comercial (no personal)',
      checkbox: 'Entiendo que esto no afectará mi puntaje de crédito',
      button: 'Obtener Mi Oferta',
      buttonLoading: 'Enviando...',
      noCreditPull: 'Sin Revisión de Crédito',
      secure: 'Seguridad de Nivel Bancario',
      fast: 'Fondos en 24 Horas',
      selectAmount: 'Seleccionar cantidad de financiamiento',
      selectRevenue: 'Seleccionar ingresos mensuales',
      selectState: 'Seleccionar estado',
      yes: 'Sí',
      no: 'No',
      reassurance: 'Aprobaciones el mismo día. Sin revisión de crédito dura. (We speak English.)',
      successMessage: '✅ ¡Solicitud recibida! Revise su correo ahora — hemos enviado instrucciones para completar su pre-aprobación del mismo día. Si lo prefiere, puede enviar sus 3 estados de cuenta bancarios más recientes directamente a antony@sparrowadvance.com.',
      errorMessage: 'Hubo un error al enviar tu solicitud. Por favor intenta de nuevo o contáctanos directamente.',
      amounts: ['$25,000 - $75,000', '$75,000 - $150,000', '$150,000 - $500,000', '$500,000+'],
      revenues: ['$10,000 - $30,000', '$30,000 - $75,000', '$75,000 - $150,000', '$150,000+'],
      validationErrors: {
        required: 'Este campo es obligatorio.',
        email: 'Por favor ingrese un correo electrónico comercial válido (ej., info@empresa.com).',
        phone: 'Por favor ingrese un número de teléfono estadounidense válido.',
        name: 'Por favor ingrese un nombre válido.',
        revenue: 'Los programas de financiamiento comienzan en $10,000 en ingresos comerciales mensuales.'
      }
    }
  };
  const t = content[language];
  const usStates = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  const validateEmail = (email: string): boolean => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return regex.test(email);
  };
  const validatePhone = (phone: string): boolean => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11 && digits[0] === '1';
  };
  const validateName = (name: string): boolean => {
    return name.length >= 4 && /^[a-zA-Z\s-]+$/.test(name);
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = t.validationErrors.required;
    } else if (!validateName(formData.fullName)) {
      newErrors.fullName = t.validationErrors.name;
    }
    if (!formData.businessName.trim()) {
      newErrors.businessName = t.validationErrors.required;
    } else if (!validateName(formData.businessName)) {
      newErrors.businessName = t.validationErrors.name;
    }
    if (!formData.email.trim()) {
      newErrors.email = t.validationErrors.required;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t.validationErrors.email;
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t.validationErrors.required;
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = t.validationErrors.phone;
    }
    if (!formData.amount) {
      newErrors.amount = t.validationErrors.required;
    }
    if (!formData.monthlyRevenue) {
      newErrors.monthlyRevenue = t.validationErrors.required;
    }
    if (!formData.state) {
      newErrors.state = t.validationErrors.required;
    }
    if (!formData.businessChecking) {
      newErrors.businessChecking = t.validationErrors.required;
    }
    if (!formData.businessConfirmation) {
      newErrors.businessConfirmation = t.validationErrors.required;
    }
    if (!agreed) {
      newErrors.agreed = t.validationErrors.required;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSubmitStatus('loading');
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'Lead');
    }
    try {
      const serviceId = 'service_otq5unq';
      const templateId = 'template_uyui7o6';
      const publicKey = 'GAU9a8WUfs7tCBUMg';
      const templateParams = {
        to_email: 'studiowebvip@gmail.com',
        cc_email: 'antony@sparrowadvance.com',
        full_name: formData.fullName,
        business_name: formData.businessName,
        phone_number: formData.phone,
        email_address: formData.email,
        funding_amount: formData.amount,
        monthly_revenue: formData.monthlyRevenue,
        state: formData.state,
        business_checking: formData.businessChecking,
        business_confirmation: formData.businessConfirmation ? 'Yes' : 'No'
      };
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }
  };
  return <section id="apply" className="w-full py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white" data-id="element-162">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-163">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-164">
          <div className="bg-gradient-to-r from-[#142B44] to-[#2D5D9F] px-4 sm:px-6 lg:px-8 py-4 sm:py-5" data-id="element-165">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 lg:gap-6" data-id="element-166">
              <div className="flex items-center space-x-2 text-white" data-id="element-167">
                <Shield size={18} className="flex-shrink-0" data-id="element-168" />
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap" data-id="element-169">
                  {t.noCreditPull}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-white" data-id="element-170">
                <Lock size={18} className="flex-shrink-0" data-id="element-171" />
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap" data-id="element-172">
                  {t.secure}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-white" data-id="element-173">
                <Clock size={18} className="flex-shrink-0" data-id="element-174" />
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap" data-id="element-175">
                  {t.fast}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8 lg:p-10" data-id="element-176">
            <div className="text-center mb-6" data-id="element-177">
              <p className="text-sm text-gray-600 font-medium mb-4" data-id="element-178">
                {t.helperText}
              </p>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-4" data-id="element-179">
                <Clock size={16} className="text-[#2D5D9F]" data-id="element-180" />
                <span className="text-sm font-semibold text-[#142B44]" data-id="element-181">
                  {t.progressHint}
                </span>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-[#142B44] mb-2 sm:mb-3" data-id="element-182">
              {t.title}
            </h2>
            <p className="text-center text-gray-600 mb-6 sm:mb-8 lg:mb-10 text-base sm:text-lg" data-id="element-183">
              {t.subtitle}
            </p>
            {submitStatus === 'success' && <div className="mb-6 p-4 sm:p-5 bg-green-50 border-2 border-green-200 rounded-xl flex items-start space-x-3" data-id="element-184">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={24} data-id="element-185" />
                <p className="text-sm sm:text-base text-green-800 font-medium leading-relaxed" data-id="element-186">
                  {t.successMessage}
                </p>
              </div>}
            {submitStatus === 'error' && <div className="mb-6 p-4 sm:p-5 bg-red-50 border-2 border-red-200 rounded-xl flex items-start space-x-3" data-id="element-187">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} data-id="element-188" />
                <p className="text-sm sm:text-base text-red-800 font-medium leading-relaxed" data-id="element-189">
                  {t.errorMessage}
                </p>
              </div>}
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 lg:space-y-6" data-id="element-190">
              <div data-id="element-191">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-192">
                  {t.fullName}
                </label>
                <div className="relative" data-id="element-193">
                  <User className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-194" />
                  <input type="text" required value={formData.fullName} onChange={e => {
                  setFormData({
                    ...formData,
                    fullName: e.target.value
                  });
                  if (errors.fullName) {
                    setErrors({
                      ...errors,
                      fullName: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} autoComplete="off" className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.fullName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="John Doe" data-id="element-195" />
                </div>
                {errors.fullName && <p className="mt-2 text-sm text-red-600" data-id="element-196">{errors.fullName}</p>}
              </div>
              <div data-id="element-197">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-198">
                  {t.businessName}
                </label>
                <div className="relative" data-id="element-199">
                  <Briefcase className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-200" />
                  <input type="text" required value={formData.businessName} onChange={e => {
                  setFormData({
                    ...formData,
                    businessName: e.target.value
                  });
                  if (errors.businessName) {
                    setErrors({
                      ...errors,
                      businessName: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} autoComplete="off" className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.businessName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="ABC Company LLC" data-id="element-201" />
                </div>
                {errors.businessName && <p className="mt-2 text-sm text-red-600" data-id="element-202">
                    {errors.businessName}
                  </p>}
              </div>
              <div data-id="element-203">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-204">
                  {t.phone}
                </label>
                <div className="relative" data-id="element-205">
                  <Phone className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-206" />
                  <input type="tel" required value={formData.phone} onChange={e => {
                  setFormData({
                    ...formData,
                    phone: e.target.value
                  });
                  if (errors.phone) {
                    setErrors({
                      ...errors,
                      phone: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} autoComplete="off" className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.phone ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="(305) 813-8377" data-id="element-207" />
                </div>
                {errors.phone && <p className="mt-2 text-sm text-red-600" data-id="element-208">{errors.phone}</p>}
              </div>
              <div data-id="element-209">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-210">
                  {t.email}
                </label>
                <div className="relative" data-id="element-211">
                  <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-212" />
                  <input type="email" required value={formData.email} onChange={e => {
                  setFormData({
                    ...formData,
                    email: e.target.value
                  });
                  if (errors.email) {
                    setErrors({
                      ...errors,
                      email: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} autoComplete="off" className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="john@company.com" data-id="element-213" />
                </div>
                {errors.email && <p className="mt-2 text-sm text-red-600" data-id="element-214">{errors.email}</p>}
              </div>
              <div data-id="element-215">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-216">
                  {t.state}
                </label>
                <div className="relative" data-id="element-217">
                  <MapPin className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-218" />
                  <select required value={formData.state} onChange={e => {
                  setFormData({
                    ...formData,
                    state: e.target.value
                  });
                  if (errors.state) {
                    setErrors({
                      ...errors,
                      state: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.state ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium appearance-none bg-white cursor-pointer text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} data-id="element-219">
                    <option value="" data-id="element-220">{t.selectState}</option>
                    {usStates.map(state => <option key={state} value={state} data-id="element-221">
                        {state}
                      </option>)}
                  </select>
                </div>
                {errors.state && <p className="mt-2 text-sm text-red-600" data-id="element-222">{errors.state}</p>}
              </div>
              <div data-id="element-223">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-224">
                  {t.monthlyRevenue}
                </label>
                <div className="relative" data-id="element-225">
                  <TrendingUp className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-226" />
                  <select required value={formData.monthlyRevenue} onChange={e => {
                  setFormData({
                    ...formData,
                    monthlyRevenue: e.target.value
                  });
                  if (errors.monthlyRevenue) {
                    setErrors({
                      ...errors,
                      monthlyRevenue: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.monthlyRevenue ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium appearance-none bg-white cursor-pointer text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} data-id="element-227">
                    <option value="" data-id="element-228">{t.selectRevenue}</option>
                    {t.revenues.map(revenue => <option key={revenue} value={revenue} data-id="element-229">
                        {revenue}
                      </option>)}
                  </select>
                </div>
                {errors.monthlyRevenue && <p className="mt-2 text-sm text-red-600" data-id="element-230">
                    {errors.monthlyRevenue}
                  </p>}
              </div>
              <div data-id="element-231">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-2 sm:mb-3 uppercase tracking-wide" data-id="element-232">
                  {t.amount}
                </label>
                <div className="relative" data-id="element-233">
                  <DollarSign className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-234" />
                  <select required value={formData.amount} onChange={e => {
                  setFormData({
                    ...formData,
                    amount: e.target.value
                  });
                  if (errors.amount) {
                    setErrors({
                      ...errors,
                      amount: ''
                    });
                  }
                }} disabled={submitStatus === 'loading'} className={`w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-4 border-2 ${errors.amount ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium appearance-none bg-white cursor-pointer text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed`} data-id="element-235">
                    <option value="" data-id="element-236">{t.selectAmount}</option>
                    {t.amounts.map(amount => <option key={amount} value={amount} data-id="element-237">
                        {amount}
                      </option>)}
                  </select>
                </div>
                {errors.amount && <p className="mt-2 text-sm text-red-600" data-id="element-238">{errors.amount}</p>}
              </div>
              <div data-id="element-239">
                <label className="block text-xs sm:text-sm font-bold text-[#142B44] mb-3 uppercase tracking-wide" data-id="element-240">
                  {t.businessChecking}
                </label>
                <div className="flex space-x-4" data-id="element-241">
                  <label className="flex items-center space-x-2 cursor-pointer" data-id="element-242">
                    <input type="radio" name="businessChecking" value="yes" checked={formData.businessChecking === 'yes'} onChange={e => {
                    setFormData({
                      ...formData,
                      businessChecking: e.target.value
                    });
                    if (errors.businessChecking) {
                      setErrors({
                        ...errors,
                        businessChecking: ''
                      });
                    }
                  }} disabled={submitStatus === 'loading'} className="w-4 h-4 text-[#2D5D9F] border-gray-300 focus:ring-[#2D5D9F]" data-id="element-243" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-244">
                      {t.yes}
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer" data-id="element-245">
                    <input type="radio" name="businessChecking" value="no" checked={formData.businessChecking === 'no'} onChange={e => {
                    setFormData({
                      ...formData,
                      businessChecking: e.target.value
                    });
                    if (errors.businessChecking) {
                      setErrors({
                        ...errors,
                        businessChecking: ''
                      });
                    }
                  }} disabled={submitStatus === 'loading'} className="w-4 h-4 text-[#2D5D9F] border-gray-300 focus:ring-[#2D5D9F]" data-id="element-246" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-247">
                      {t.no}
                    </span>
                  </label>
                </div>
                {errors.businessChecking && <p className="mt-2 text-sm text-red-600" data-id="element-248">
                    {errors.businessChecking}
                  </p>}
              </div>
              <div className="flex items-start space-x-3 bg-blue-50 p-4 sm:p-5 rounded-xl border-2 border-blue-200" data-id="element-249">
                <input type="checkbox" required checked={formData.businessConfirmation} onChange={e => {
                setFormData({
                  ...formData,
                  businessConfirmation: e.target.checked
                });
                if (errors.businessConfirmation) {
                  setErrors({
                    ...errors,
                    businessConfirmation: ''
                  });
                }
              }} disabled={submitStatus === 'loading'} className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-[#2D5D9F] border-gray-300 rounded focus:ring-[#2D5D9F] cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" data-id="element-250" />
                <label className="text-xs sm:text-sm text-gray-700 font-medium leading-relaxed" data-id="element-251">
                  {t.businessConfirmation}
                </label>
              </div>
              {errors.businessConfirmation && <p className="mt-2 text-sm text-red-600" data-id="element-252">
                  {errors.businessConfirmation}
                </p>}
              <div className="flex items-start space-x-3 bg-green-50 p-4 sm:p-5 rounded-xl border-2 border-green-200" data-id="element-253">
                <input type="checkbox" required checked={agreed} onChange={e => {
                setAgreed(e.target.checked);
                if (errors.agreed) {
                  setErrors({
                    ...errors,
                    agreed: ''
                  });
                }
              }} disabled={submitStatus === 'loading'} className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" data-id="element-254" />
                <label className="text-xs sm:text-sm text-gray-700 font-medium leading-relaxed" data-id="element-255">
                  {t.checkbox}
                </label>
              </div>
              {errors.agreed && <p className="mt-2 text-sm text-red-600" data-id="element-256">{errors.agreed}</p>}
              <button type="submit" disabled={submitStatus === 'loading'} className="w-full py-4 sm:py-5 bg-gradient-to-r from-[#142B44] to-[#2D5D9F] text-white font-bold text-base sm:text-lg rounded-xl hover:from-[#2D5D9F] hover:to-[#142B44] transition-all shadow-xl hover:shadow-2xl hover:scale-105 duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-257">
                {submitStatus === 'loading' ? t.buttonLoading : t.button}
              </button>
              <p className="text-center text-xs sm:text-sm text-gray-600 font-medium mt-3 sm:mt-4" data-id="element-258">
                {t.reassurance}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>;
}